import { memo, useMemo } from 'react';
import { AnalyticsCommonTypes, PracticeAnalyticsAggregations, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { DemoChip } from '@frontend/analytics-shared';
import { AreaChartData, Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { trackingIds } from '../../tracking-ids';
import { CompareLocationsButton } from '../compare-locations-button';
import { usePaNavigate } from './hooks/use-pa-navigate';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { useMonthlyTrendChartModal } from './monthly-trend-chart-modal';
import { PracticeAnalyticsInfoTips } from './practice-analytics-info-tips';

type Props = PracticeAnalyticsTypes.MetricProps & {
  colors: AnalyticsCommonTypes.StringRecord;
  data?: ReturnType<typeof PracticeAnalyticsAggregations.newPatientsSummary>;
  isLoading?: boolean;
  labels: AnalyticsCommonTypes.StringRecord;
};

export const NewPatientsMetricSingle = memo(
  ({ clickNoop, colors, data, isDrillDownPage, isLoading, labels }: Props) => {
    const { t } = useTranslation('analytics');
    const navigate = usePaNavigate();
    const { filters, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore('filters', 'showDemoChipAndBanner');

    const monthlyTrendModal = useMonthlyTrendChartModal({
      defaultKey: 'total',
      metric: 'newPatients',
      tabs: { total: t('Patients'), production: t('Production') },
      title: t('Trend: New Patients'),
      trackingId: trackingIds.practiceAnalytics.newPatientsTrend,
    });

    const multipleLocationsSelected = (filters.locations?.length || 0) > 1;

    const chartData = useMemo(() => {
      const { newPatients } = data?.aggregatedData || {};

      const processedData = newPatients?.historicalData.reduce(
        (acc, _, index) => {
          const x = formatters.date(newPatients.totals.productionPerDay[index]?.date, true);

          return {
            ...acc,
            groups: [
              ...(acc.groups || []),
              {
                name: x,
                values: {
                  newPatients: newPatients.historicalData[index]?.total || 0,
                  completedProduction: newPatients.totals.productionPerDay[index]?.amount || 0,
                  uncapturedProduction: newPatients.totals.unscheduledProductionPerDay[index]?.amount || 0,
                },
              },
            ],
          };
        },
        {
          groups: [],
        } as AreaChartData
      );

      return {
        data: processedData,
        totalCompletedProduction: newPatients?.totals?.production || 0,
        totalNewPatients: newPatients?.totals?.patients || 0,
        totalUncapturedProduction:
          newPatients?.totals.unscheduledProductionPerDay.reduce((sum, { amount }) => sum + amount, 0) || 0,
      };
    }, [data]);

    return (
      <>
        <Chart
          colors={colors}
          isLoading={isLoading}
          labels={labels}
          onClick={
            clickNoop
              ? undefined
              : () => {
                  navigate({
                    to: '/analytics/practice/new-patients',
                  });
                }
          }
          trackingId={trackingIds.practiceAnalytics.newPatientsChart}
        >
          {isDrillDownPage ? (
            showDemoChipAndBanner && <DemoChip />
          ) : (
            <Chart.Header
              actions={[monthlyTrendModal.triggerProps]}
              bottomElement={
                multipleLocationsSelected ? <CompareLocationsButton style={{ marginBottom: theme.spacing(2) }} /> : null
              }
              infoTip={<PracticeAnalyticsInfoTips tip='newPatients' />}
              leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
              title={t('New Patients')}
            />
          )}
          <Chart.AreaChart
            appearance={{
              height: 180,
              showXAxis: true,
              showYAxis: true,
            }}
            data={chartData.data}
            formatValue={{
              newPatients: formatters.value,
              completedProduction: (v) => formatters.currency(v),
              uncapturedProduction: (v) => formatters.currency(v),
            }}
            multiAxisClubIds={{
              newPatients: 'patients',
              completedProduction: 'production',
              uncapturedProduction: 'production',
            }}
          />
          <Chart.Legends
            customData={{
              newPatients: {
                value: formatters.value(chartData?.totalNewPatients),
              },
              completedProduction: {
                value: formatters.currency(chartData?.totalCompletedProduction),
              },
              uncapturedProduction: {
                value: formatters.currency(chartData?.totalUncapturedProduction),
              },
            }}
          />
        </Chart>
        {monthlyTrendModal.modalRenderer()}
      </>
    );
  }
);

NewPatientsMetricSingle.displayName = 'NewPatientsMetricSingle';
